import { HTMLCustomElement } from '@emartech/ui-framework-utils';
import emojiConvert from '../../utils/emoji-convert';
import CustomEvent from 'custom-event';
import JSLogger from '../../utils/jslogger';
const logger = new JSLogger('decommission');

class ETokenInput extends HTMLCustomElement {
  init() {
    this._empty = '\u200B';
    this._padding = null;
    this._nostyle = false;
    this._textarea = false;
    this._rows = 1;
    this._placeholder = '';
    this._activeToken = null;
    this._selectedToken = null;

    this._dom = {};
    this._dom.editable = this._createEditableElement();
    this._dom.content = this._createContentElement();

    this._events = {};
    this._events.handlePadding = this._handlePadding.bind(this);
    this._events.handleMousePadding = this._handleMousePadding.bind(this);
    this._events.handleKeys = this._handleKeys.bind(this);
    this._events.cleanupHTML = this._cleanupHTML.bind(this);
    this._events.blur = this._blur.bind(this);
    this._events.focus = this._focus.bind(this);
    this._events.paste = this._paste.bind(this);

    this.addEventListener('click', () => this._dom.editable.focus());
    this._dom.editable.addEventListener('keydown', this._events.handleKeys);
    this._dom.editable.addEventListener('keydown', this._events.handlePadding);
    this._dom.editable.addEventListener('mousedown', () => this._clearSelectedToken());
    this._dom.editable.addEventListener('mouseup', this._events.handleMousePadding);
    this._dom.editable.addEventListener('blur', this._events.blur);
    this._dom.editable.addEventListener('paste', this._events.paste);
    this._dom.editable.addEventListener('input', this._events.cleanupHTML);
    this._dom.editable.addEventListener('focus', this._events.focus);
    this._dom.editable.addEventListener('keyup', this._events.cleanupHTML);
  }

  connectedCallback() {
    this.appendChild(this._dom.editable);
    this.appendChild(this._dom.content);
    this._handleContent();
    logger.log('ETokenInput');
  }

  static get observedAttributes() {
    return ['value', 'placeholder', 'no-style', 'rows', 'textarea', 'height'];
  }

  set value(value) {
    if (value === this.value) { return; }

    const tempElement = document.createElement('div');
    tempElement.innerHTML = value;
    const tokens = tempElement.querySelectorAll('span');
    [].forEach.call(tokens, (token) => {
      const isIcon = token.getAttribute('type') === 'icon';
      const icon = token.getAttribute('icon');
      const iconTooltip = token.getAttribute('icon-tooltip');
      token.textContent = isIcon ? '\u00A0\u00A0\u00A0' : token.getAttribute('name');

      if (icon) {
        const iconElement = document.createElement('e-icon');
        iconElement.setAttribute('icon', icon);
        iconElement.setAttribute('size', 'small');
        iconElement.setAttribute('color', 'inherit');

        if (iconTooltip) {
          const iconTooltipElement = document.createElement('e-tooltip');
          iconTooltipElement.setAttribute('content', iconTooltip);
          iconTooltipElement.appendChild(iconElement);
          iconTooltipElement.className = 'e-tokeninput__icon';
          token.appendChild(iconTooltipElement);
        } else {
          iconElement.className = 'e-tokeninput__icon';
          token.appendChild(iconElement);
        }
      }

      token.contentEditable = false;
      token.className = 'e-tokeninput__token';
      token.setAttribute('token', 'true');
    });
    this._dom.editable.innerHTML = tempElement.innerHTML;
    this._handleContent();
    this._change();
  }

  get value() {
    return emojiConvert.encodeEntities(this._cleanupValue(this._dom.editable));
  }

  set activeToken(element) {
    if (!element) {
      this._deactivateToken();
      return;
    }

    this._activateToken(element);
  }

  get activeToken() {
    return this._activeToken;
  }

  get selectionCollapsed() {
    const selection = window.getSelection();
    if (selection.focusNode === null) {
      return false;
    }

    return selection.getRangeAt(0).collapsed;
  }

  get placeholder() {
    return this._placeholder;
  }

  set placeholder(value) {
    value = value || '';
    this._placeholder = value;

    if (this.value) { return; }

    if (value !== '') {
      this.setAttribute('fake-placeholder', value);
    } else {
      this.removeAttribute('fake-placeholder');
    }
  }

  set noStyle(value) {
    this._nostyle = super._convertAttributeToBoolean(value);
    this._dom.editable.classList.toggle('e-input', !this._nostyle);
    this._dom.content.classList.toggle('e-input', !this._nostyle);
  }

  set rows(value) {
    this._rows = parseInt(value);
    this._dom.editable.classList.toggle('e-tokeninput__editable-multiline', this._rows !== 1);
    this._dom.content.classList.toggle('e-tokeninput__content-multiline', this._rows !== 1);
    this._handleContent();
  }

  set textarea(value) {
    this._textarea = super._convertAttributeToBoolean(value);
    this._dom.editable.classList.toggle('e-input-textarea', this._textarea);
    this._dom.content.classList.toggle('e-input-textarea', this._textarea);
  }

  set height(value) {
    this._dom.editable.style.height = value;
    this._dom.content.style.height = value;
  }

  _handleContent() {
    this._dom.content.innerHTML = this._dom.editable.innerHTML;
    this._truncate();
  }

  _truncate() {
    if (!this._dom.content.parentNode) { return; }

    const textContent = this._dom.content.textContent;
    const rowHeight = this._getRowHeight();
    const availableHeight = rowHeight * this._rows;
    this.style.maxHeight = availableHeight + 'px';

    let position = this._findOutOfBoundCharacterPosition(rowHeight, availableHeight);
    while (availableHeight < this._contentHeight && position > 0) {
      this._setTruncatedContent(textContent, position);
      position -= 1;
    }
  }

  _findOutOfBoundCharacterPosition(rowHeight, availableHeight) {
    if (availableHeight >= this._contentHeight) { return 0; }

    const textContent = this._dom.content.textContent;
    const originalHeight = this._contentHeight;
    const characterNumberPerRow = Math.round(textContent.length * (rowHeight / originalHeight));
    let position = characterNumberPerRow * this._rows;

    this._setTruncatedContent(textContent, position);
    while (availableHeight >= this._contentHeight && position <= textContent.length) {
      position += characterNumberPerRow;
      this._setTruncatedContent(textContent, position);
    }

    return position;
  }

  _setTruncatedContent(content, position) {
    const trimmedContent = content.slice(0, position - 3) + '&#8230';
    this._dom.content.innerHTML = this._buildTextWithTokens(trimmedContent);
  }

  get _contentHeight() {
    return this._dom.content.offsetHeight;
  }

  _getRowHeight() {
    this._dom.content.style.display = 'block';
    this._dom.content.style.whiteSpace = 'nowrap';
    const rowHeight = this._contentHeight;
    this._dom.content.style.removeProperty('display');
    this._dom.content.style.removeProperty('white-space');
    return rowHeight;
  }

  _buildTextWithTokens(text) {
    const tokenPositions = this._collectTokenPositions();
    return tokenPositions.reduce((prev, token) => {
      return prev.substr(0, token.startPosition) + token.html + prev.substr(token.endPosition);
    }, text);
  }

  _collectTokenPositions() {
    const clonedContainer = this._dom.editable.cloneNode(true);
    const tokenElements = clonedContainer.querySelectorAll('.e-tokeninput__token');
    const cleanHTML = clonedContainer.innerHTML.replace('&nbsp;', ' ');

    return [].reduce.call(tokenElements, (prev, element) => {
      const tokenData = prev.concat({
        html: element.outerHTML,
        startPosition: cleanHTML.indexOf(element.outerHTML),
        endPosition: cleanHTML.indexOf(element.outerHTML) + element.textContent.length
      });

      element.outerHTML = element.innerHTML;
      return tokenData;
    }, []);
  }

  _activateToken(element) {
    this._deactivateToken();
    element.classList.add('e-tokeninput__token-active');
    this._activeToken = element;

    const event = new CustomEvent('activateToken', {
      detail: {
        name: element.getAttribute('name'),
        type: element.getAttribute('type'),
        icon: element.getAttribute('icon'),
        iconTooltip: element.getAttribute('icon-tooltip'),
        data: this._getData(element)
      }
    });
    this.dispatchEvent(event);
  }

  _getData(element) {
    const data = element.getAttribute('data');
    try {
      return JSON.parse(data);
    } catch (error) {
      return data;
    }
  }

  _deactivateToken() {
    if (!this.activeToken) { return; }

    this.activeToken.classList.remove('e-tokeninput__token-active');
    this._activeToken = false;
  }

  _handleKeys(event) {
    const selection = window.getSelection();
    const keyCodeMap = {
      13: 'enter',
      27: 'esc',
      66: 'b',
      73: 'i',
      37: 'left',
      39: 'right',
      8: 'backspace',
      32: 'space'
    };

    const handleKeyboardClick = () => {
      if (!this._selectedToken) { return; }

      event.preventDefault();

      this.activeToken = this._selectedToken;
    };

    const getPreviousTokenRange = () => {
      const range = this._previousElementRange(selection);
      if (!range) { return false; }

      const previousNode = range.cloneContents().lastChild;
      if (previousNode && this._isToken(previousNode)) {
        return range;
      }

      return false;
    };

    const handle = {
      backspace: () => {
        const previousRange = getPreviousTokenRange();
        if (previousRange && this.selectionCollapsed) {
          previousRange.deleteContents();
          event.preventDefault();
        }
      },
      left: () => {
        if (!this.selectionCollapsed && !this._selectedToken) { return; }

        if (this._activeToken) {
          this.activeToken = null;
        }

        if (this._selectedToken) {
          event.preventDefault();

          if (this._selectedToken.previousSibling && !this._isToken(this._selectedToken.previousSibling)) {
            const textLength = this._selectedToken.previousSibling.length;
            this._setRange(this._selectedToken.previousSibling, textLength, textLength);
          } else {
            this._padding = this._insertPadding('left', this._selectedToken);
            this._clearSelectedToken();
            return;
          }

          this._clearSelectedToken();
        }

        const previousNode = selection.focusNode.previousSibling;
        const previousNodeIsToken = this._isToken(previousNode);
        const isStartOfCurrentText = selection.focusOffset === 0 || selection.focusNode.textContent === this._empty;

        if (previousNodeIsToken && isStartOfCurrentText) {
          event.preventDefault();

          this._setSelectedToken(previousNode);
          selection.removeAllRanges();

          return;
        }

        const previousRange = getPreviousTokenRange();
        if (previousRange && this.selectionCollapsed) {
          selection.removeAllRanges();
          selection.addRange(previousRange);
        }
      },
      right: () => {
        if (!this.selectionCollapsed && !this._selectedToken) { return; }

        if (this._activeToken) {
          this.activeToken = null;
        }

        if (this._selectedToken) {
          event.preventDefault();

          if (this._selectedToken.nextSibling && !this._isToken(this._selectedToken.nextSibling)) {
            this._setRange(this._selectedToken.nextSibling, 0, 0);
          } else {
            this._padding = this._insertPadding('right', this._selectedToken);
            this._clearSelectedToken();
            return;
          }

          this._clearSelectedToken();
        }

        const nextNode = selection.focusNode.nextSibling;
        const nextNodeIsToken = this._isToken(nextNode);
        const isEndOfCurrentText = selection.focusOffset === selection.focusNode.length ||
          selection.focusNode.textContent === this._empty;

        if (nextNodeIsToken && isEndOfCurrentText) {
          event.preventDefault();

          this._setSelectedToken(nextNode);
          selection.removeAllRanges();

          return;
        }

        const isOnLastCharacter = selection.focusOffset === selection.focusNode.textContent.length;

        if (isOnLastCharacter && nextNodeIsToken && nextNode === this._dom.editable.lastChild) {
          this._padding = this._insertPadding('right', nextNode);
        };
      },
      enter: () => {
        event.preventDefault();
        handleKeyboardClick();
      },
      space: () => {
        handleKeyboardClick();
      },
      esc: () => {
        this._dom.editable.blur();
      },
      b: () => {
        if (event.metaKey || event.ctrlKey) {
          event.preventDefault();
        }
      },
      i: () => {
        if (event.metaKey || event.ctrlKey) {
          event.preventDefault();
        }
      },
      default: () => false
    };

    let key = keyCodeMap[event.keyCode];
    (handle[key] || handle.default)();
    this._handlePlaceholder();
  }

  _previousElementRange(selection) {
    if (selection.focusNode === null) { return false; }

    const curRange = selection.getRangeAt(selection.rangeCount - 1);
    if (curRange.commonAncestorContainer.nodeType === 3 && curRange.startOffset > 0) { return false; }

    const range = document.createRange();
    if (selection.anchorNode !== this._dom.editable) {
      range.selectNodeContents(this._dom.editable);
      range.setEndBefore(selection.anchorNode);
    } else if (selection.anchorOffset > 0) {
      range.setEnd(this._dom.editable, selection.anchorOffset);
    } else {
      return false;
    }

    if (range.endOffset === 0) { return false; }

    range.setStart(this._dom.editable, range.endOffset - 1);
    return range;
  }

  _isCursorOnToken(element, offset) {
    if (!this._isToken(element)) { return; }

    const direction = offset === 0 ? 'left' : 'right';
    this._padding = this._insertPadding(direction, element);
  }

  _isCursorLeftComponent(element) {
    if (this.contains(element)) { return; }

    const token = this._dom.editable.lastChild;
    this._padding = this._insertPadding('right', token);
  }

  _isCursorOnEditable(element, offset) {
    if (element !== this._dom.editable) { return; }

    const textNodes = this._textNodes();
    const tokenText = textNodes[offset];
    const firstToken = this._dom.editable.firstChild;
    const lastToken = this._dom.editable.lastChild;

    if (tokenText && tokenText.parentNode && this._isToken(tokenText.parentNode)) {
      const previousElement = tokenText.parentNode.previousSibling;
      const previousIsText = previousElement && previousElement.nodeType === 3;
      if (previousIsText) {
        this._setRange(previousElement, previousElement.length, previousElement.length);
        return;
      }

      this._padding = this._insertPadding('left', tokenText.parentNode);
      return;
    }

    const direction = offset === 0 ? 'left' : 'right';
    const token = offset === 0 ? firstToken : lastToken;
    this._padding = this._insertPadding(direction, token);
  }

  _handlePadding(event) {
    if (event.key === 'Tab' || event.key === 'Escape') { return; }

    this._cleanupPadding();
    setTimeout(() => {
      this._cleanupDirtyNodes();
      this._saveRange();
      const selection = window.getSelection();
      const node = selection.focusNode;
      const offset = selection.focusOffset;

      if (!this.selectionCollapsed) {
        return;
      }

      this._isCursorOnToken(node.parentNode, offset);
      this._isCursorLeftComponent(node.parentNode);
      this._isCursorOnEditable(node, offset);
    }, 10);
  }

  _handleMousePadding(event) {
    const selection = window.getSelection();
    this._cleanupPadding();
    this._saveRange();
    const rangeContent = selection.getRangeAt(0).cloneContents();
    const isTokenOnly = rangeContent.childNodes.length === 1 && this._isToken(rangeContent.lastChild);
    if (!this.selectionCollapsed && !isTokenOnly) {
      return;
    }

    const tokens = this._dom.editable.querySelectorAll('.e-tokeninput__token');
    this.activeToken = [...tokens].find(token => token === event.target || token.contains(event.target));
    this._toggleSelectedToken(this.activeToken);

    if (this.activeToken) {
      selection.removeAllRanges();
      return;
    }

    if (this._isToken(selection.focusNode)) {
      this._padding = this._insertPadding('right', selection.focusNode, true);
      return;
    }

    const offset = selection.focusOffset;
    const firstElement = this._dom.editable.firstChild;
    const lastElement = this._dom.editable.lastChild;

    if (firstElement && event.layerX < firstElement.offsetLeft) {
      this._padding = this._insertPadding('left', firstElement);
      return;
    }

    const direction = offset === 0 ? 'left' : 'right';
    const element = offset === 0 ? firstElement : lastElement;

    if (selection.focusNode === this._dom.editable) {
      this._padding = this._insertPadding(direction, element);
    }
  }

  _focus() {
    setTimeout(() => {
      if (this._selectedToken) {
        const selection = window.getSelection();
        selection.removeAllRanges();
      }
    }, 10);

    this._dom.editable.classList.add('e-tokeninput__editable-active');
    this.style.removeProperty('max-height');
  }

  _blur() {
    this._cleanupPadding();
    this.activeToken = false;
    this._dom.editable.classList.remove('e-tokeninput__editable-active');
    this._editableJumpToStart();
    this._handleContent();
    const event = new CustomEvent('blur');
    this.dispatchEvent(event);
  }

  _editableJumpToStart() {
    this._dom.editable.style.whiteSpace = 'normal';
    this._dom.editable.offsetHeight; // eslint-disable-line no-unused-expressions
    this._dom.editable.style.removeProperty('white-space');
  }

  _paste(event) {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    let text = '';

    if (window.clipboardData && window.clipboardData.getData) {
      text = window.clipboardData.getData('Text');
    } else if (event.clipboardData && event.clipboardData.getData) {
      text = event.clipboardData.getData('text/plain');
    }

    text = text.trim().replace(/(\r\n|\n|\r)/gm, '');

    const textNode = document.createTextNode(text);
    event.preventDefault();
    range.deleteContents();
    range.insertNode(textNode);
    this._setRange(textNode, textNode.length, textNode.length);
    this._change();
  }

  _createEditableElement() {
    const element = document.createElement('div');
    element.className = 'e-tokeninput__editable';
    element.contentEditable = true;

    if (!this._nostyle) {
      element.className += ' e-input';
    }

    if (this._textarea) {
      element.className += ' e-input-textarea';
    }

    return element;
  }

  _createContentElement() {
    const element = document.createElement('div');
    element.className = 'e-tokeninput__content';

    if (!this._nostyle) {
      element.className += ' e-input';
    }

    if (this._textarea) {
      element.className += ' e-input-textarea';
    }

    return element;
  }

  _isToken(node) {
    return node?.nodeType === 1 && node?.getAttribute('token');
  }

  _saveRange() {
    const selection = window.getSelection();
    if (selection.focusNode === null) {
      return false;
    }

    this._range = selection.getRangeAt(0).cloneRange();
  }

  _restoreRange() {
    if (!this._range) {
      return;
    }

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(this._range);
  }

  _insertPadding(direction, element, force = false) {
    if ((!element || !direction || !this._isToken(element)) && !force) {
      return null;
    }

    const padding = document.createTextNode(this._empty);
    const insert = {
      left: () => this._insertBefore(padding, element),
      right: () => this._insertAfter(padding, element)
    };
    insert[direction]();
    this._setRange(padding, this._empty.length, this._empty.length);
    return padding;
  }

  _cleanupPadding() {
    if (!this._padding) { return; }

    this._padding.parentNode.removeChild(this._padding);
    this._padding = null;
  }

  _insertBefore(newElement, targetElement) {
    const parent = targetElement.parentNode;
    parent.insertBefore(newElement, targetElement);
  }

  _insertAfter(newElement, targetElement) {
    const parent = targetElement.parentNode;
    if (parent.lastChild === targetElement) {
      parent.appendChild(newElement);
    } else {
      parent.insertBefore(newElement, targetElement.nextSibling);
    }
  }

  _setRange(element, start, end) {
    const selection = window.getSelection();
    const range = document.createRange();
    range.setStart(element, start);
    range.setEnd(element, end);
    selection.removeAllRanges();
    selection.addRange(range);
    return range;
  }

  _textNodes() {
    const walk = document.createTreeWalker(this._dom.editable, NodeFilter.SHOW_ALL, null, false);
    let node = null;
    let nodes = [];
    while (node = walk.nextNode()) {
      if (node.nodeType === 3) {
        nodes.push(node);
      }
    }
    return nodes;
  }

  _cleanupDirtyNodes() {
    const children = this._dom.editable.childNodes;
    [].slice.call(children).forEach((node) => {
      const isEmptyTextNode = node.nodeType === 3 && node.textContent === '';
      const isMozNode = node.nodeType === 1 && node.getAttribute('type') === '_moz';
      if (isEmptyTextNode || isMozNode) {
        node.parentNode.removeChild(node);
      }
    });
  }

  _cleanupHTML() {
    const children = this._dom.editable.children;
    [].slice.call(children).forEach((node) => {
      if (!this._isToken(node) && node.nodeName !== 'BR') {
        const textNode = document.createTextNode(node.textContent);
        this._dom.editable.replaceChild(textNode, node);
      }
    });
    this._change();
  }

  _handlePlaceholder() {
    const placeholderValue = this.value ? '' : this.placeholder;
    this.setAttribute('fake-placeholder', placeholderValue);
  }

  _change() {
    this._handlePlaceholder();
    const event = new CustomEvent('valueChange', {
      detail: {
        value: this.value
      }
    });
    this.dispatchEvent(event);
  }

  _cleanupValue(element) {
    const cleanElement = element.cloneNode(true);
    const children = cleanElement.childNodes;
    [].slice.call(children).forEach((node) => {
      const isEmptyElement = node.nodeType === 3 && node.textContent.indexOf(this._empty) !== -1;
      const isHTMLElement = node.nodeType === 1 && !this._isToken(node);
      if (isHTMLElement) {
        node.parentNode.removeChild(node);
      }

      if (isEmptyElement) {
        const empty = new RegExp(this._empty, 'g');
        node.textContent = node.textContent.replace(empty, '');
      }

      if (this._isToken(node)) {
        node.removeAttribute('class');
        node.removeAttribute('contenteditable');
        node.removeAttribute('token');
      }
    });
    return this._cleanupNbps(cleanElement.innerHTML);
  }

  _cleanupNbps(value) {
    return value.replace(/&nbsp;/g, ' ');
  }

  _removeSelectedToken() {
    const range = window.getSelection().getRangeAt(0);
    const possibleToken = range.commonAncestorContainer.parentNode;
    if (this._isToken(possibleToken)) {
      possibleToken.parentNode.removeChild(possibleToken);
    }
  }

  _setSelectedToken(node) {
    this._selectedToken = node;
    this._selectedToken.classList.add('e-tokeninput__token-selected');
  }

  _clearSelectedToken() {
    if (!this._selectedToken) { return; }

    this._selectedToken.classList.remove('e-tokeninput__token-selected');
    this._selectedToken = null;
  }

  _toggleSelectedToken(node) {
    if (node) {
      this._setSelectedToken(node);
      return;
    }

    this._clearSelectedToken();
  }

  _insertNode(node) {
    this._dom.editable.focus();
    this._restoreRange();
    this._removeSelectedToken();

    if (this._range) {
      this._range.deleteContents();
      this._range.insertNode(node);
      this._range.collapse(false);
    } else {
      this._dom.editable.appendChild(node);
    }
  }

  insertText(text) {
    const textNode = document.createTextNode(text);
    this._insertNode(textNode);
    this._setRange(textNode, textNode.length, textNode.length);
    this._change();
  }

  insert(data) {
    const token = this._createToken(data);
    this._insertNode(token);
    this._padding = this._insertPadding('right', token);
    this._change();
    this._handleContent();
  }


  _createToken(data) {
    const token = document.createElement('span');
    token.contentEditable = false;
    token.className = 'e-tokeninput__token';
    token.setAttribute('e-token', '');
    token.setAttribute('token', 'true');
    token.setAttribute('name', data.name);
    token.setAttribute('type', data.type);
    token.setAttribute('data', typeof data.data === 'object' ? JSON.stringify(data.data) : data.data);
    token.textContent = data.type === 'icon' ? '\u00A0\u00A0\u00A0' : data.name;

    if (data.icon) {
      token.setAttribute('icon', data.icon);

      const iconElement = document.createElement('e-icon');
      iconElement.setAttribute('icon', data.icon);
      iconElement.setAttribute('size', 'small');
      iconElement.setAttribute('color', 'inherit');

      if (data.iconTooltip) {
        token.setAttribute('icon-tooltip', data.iconTooltip);
        const iconTooltipElement = document.createElement('e-tooltip');
        iconTooltipElement.setAttribute('content', data.iconTooltip);
        iconTooltipElement.appendChild(iconElement);
        iconTooltipElement.className = 'e-tokeninput__icon';
        token.appendChild(iconTooltipElement);
      } else {
        iconElement.className = 'e-tokeninput__icon';
        token.appendChild(iconElement);
      }
    }

    return token;
  }
}

export default ETokenInput;
